'use client';

import { useEffect, useRef, useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { useTheme } from '@/context/ThemeContext';
import { useTenantSettings } from '@/lib/hooks/useTenantSettings';
import { useAuthSession } from '@/lib/hooks/useAuthSession';
import { mapPrimaryColorFromApi } from '@/lib/tenantTheme';
import { setCurrencySymbol as persistCurrencySymbol } from '@/lib/currency';
import { resolveTenantMediaUrl } from '@/lib/resolveMediaUrl';

type Session = ReturnType<typeof useAuthSession>;

type Props = {
  session: Session;
  onComplete: () => void;
};

type Phase = 'app' | 'company' | 'primaryFill' | 'fadeOut';

/**
 * Full-screen splash while auth session resolves, then a short branded handoff:
 * app logo → company logo → full primary background → fade out before the shell appears.
 */
export default function AuthSessionLoadingSplash({ session, onComplete }: Props) {
  const { setPrimaryColor } = useTheme();
  const tenantSettings = useTenantSettings({ enabled: session.isSuccess });
  const sequenceStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [phase, setPhase] = useState<Phase>('app');
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);

  useEffect(() => {
    if (!session.isSuccess) return;
    if (sequenceStartedRef.current) return;

    const timers: number[] = [];
    let cancelled = false;

    const clearAll = () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };

    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
      timers.push(id);
    };

    const startSequence = () => {
      if (cancelled || sequenceStartedRef.current) return;
      sequenceStartedRef.current = true;

      const t = tenantSettings.data;
      if (t) {
        setPrimaryColor(mapPrimaryColorFromApi(t.primaryColor));
        persistCurrencySymbol((t.currencySymbol ?? 'Rs').toString());
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          setPhase('company');
          schedule(() => setPhase('primaryFill'), 520);
          schedule(() => setPhase('fadeOut'), 1120);
          schedule(() => onCompleteRef.current(), 1680);
        });
      });
    };

    if (tenantSettings.isFetched) {
      startSequence();
    } else {
      schedule(startSequence, 900);
    }

    return clearAll;
  }, [session.isSuccess, tenantSettings.isFetched, tenantSettings.data, setPrimaryColor]);

  const logoUrl = tenantSettings.data?.logoUrl ?? null;
  const resolvedLogoUrl = resolveTenantMediaUrl(logoUrl);
  const companyName = (tenantSettings.data?.companyName ?? '').trim() || 'Your workspace';

  useEffect(() => {
    setCompanyLogoFailed(false);
  }, [logoUrl]);

  const bgClass =
    phase === 'app'
      ? 'bg-gradient-to-b from-[hsl(var(--primary)/0.12)] via-[hsl(var(--background))] to-[hsl(var(--background))]'
      : phase === 'company'
        ? 'bg-gradient-to-b from-[hsl(var(--primary)/0.18)] via-[hsl(var(--background))] to-[hsl(var(--background))]'
        : phase === 'primaryFill'
          ? 'bg-[hsl(var(--primary))]'
          : 'bg-[hsl(var(--primary))]';

  const overlayOpacity =
    phase === 'fadeOut' ? 'opacity-0 pointer-events-none' : 'opacity-100';

  const content = (
    <>
      <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:106:6" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="106" data-component-end-line="169" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20min-h-0%20w-full%20max-w-%5Bmin(100%25%2C28rem)%5D%20flex-1%20flex-col%20items-center%20justify-center%20transition-colors%20duration-%5B520ms%5D%20ease-out%22%7D"
        className="flex min-h-0 w-full max-w-[min(100%,28rem)] flex-1 flex-col items-center justify-center transition-colors duration-[520ms] ease-out"
        aria-hidden={phase === 'fadeOut'}
      >
        <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:110:8" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="110" data-component-end-line="168" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex%20min-h-%5B260px%5D%20w-full%20flex-col%20items-center%20justify-center%20px-1%20sm%3Apx-0%22%7D" className="relative flex min-h-[260px] w-full flex-col items-center justify-center px-1 sm:px-0">
          <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:111:10" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="111" data-component-end-line="123" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-500 ease-out ${
              phase === 'app' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            }`}
          >
            <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:116:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="116" data-component-end-line="118" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B1.75rem%5D%20bg-%5Bhsl(var(--card))%5D%20p-5%20shadow-lg%20shadow-%5Bhsl(var(--primary)%2F0.12)%5D%20ring-1%20ring-%5Bhsl(var(--border))%5D%22%7D" className="rounded-[1.75rem] bg-[hsl(var(--card))] p-5 shadow-lg shadow-[hsl(var(--primary)/0.12)] ring-1 ring-[hsl(var(--border))]">
              <AppLogo data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:117:14" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="117" data-component-end-line="117" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="AppLogo" data-component-content="%7B%22elementName%22%3A%22AppLogo%22%7D" size={72} />
            </div>
            <h1 data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:119:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="119" data-component-end-line="121" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22mt-8%20w-full%20max-w-%5Bmin(100%25%2C22rem)%5D%20px-2%20text-center%20text-xl%20font-semibold%20tracking-tight%20text-%5Bhsl(var(--foreground))%5D%22%2C%22textContent%22%3A%22Rentals%22%7D" className="mt-8 w-full max-w-[min(100%,22rem)] px-2 text-center text-xl font-semibold tracking-tight text-[hsl(var(--foreground))]">
              Rentals
            </h1>
            <p data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:122:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="122" data-component-end-line="122" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-2%20text-sm%20text-%5Bhsl(var(--muted-foreground))%5D%22%2C%22textContent%22%3A%22Preparing%20your%20workspace%E2%80%A6%22%7D" className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Preparing your workspace…</p>
          </div>

          <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:125:10" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="125" data-component-end-line="167" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-500 ease-out ${
              phase === 'app' ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
            }`}
          >
            <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:130:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="130" data-component-end-line="148" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B1.75rem%5D%20bg-%5Bhsl(var(--card))%5D%20p-5%20shadow-xl%20shadow-black%2F10%20ring-1%20ring-%5Bhsl(var(--border))%5D%22%7D" className="rounded-[1.75rem] bg-[hsl(var(--card))] p-5 shadow-xl shadow-black/10 ring-1 ring-[hsl(var(--border))]">
              <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:131:14" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="131" data-component-end-line="147" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20size-%5B72px%5D%20shrink-0%20items-center%20justify-center%20overflow-hidden%20rounded-2xl%22%7D" className="flex size-[72px] shrink-0 items-center justify-center overflow-hidden rounded-2xl">
                {resolvedLogoUrl && !companyLogoFailed ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:134:18" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="134" data-component-end-line="141" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AresolvedLogoUrl%5D%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22size-full%20object-contain%22%7D"
                    src={resolvedLogoUrl}
                    alt=""
                    className="size-full object-contain"
                    referrerPolicy="no-referrer"
                    decoding="async"
                    onError={() => setCompanyLogoFailed(true)}
                  />
                ) : (
                  <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:143:18" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="143" data-component-end-line="145" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20size-full%20items-center%20justify-center%20bg-%5Bhsl(var(--primary))%5D%20text-lg%20font-bold%20text-%5Bhsl(var(--primary-foreground))%5D%22%7D" className="flex size-full items-center justify-center bg-[hsl(var(--primary))] text-lg font-bold text-[hsl(var(--primary-foreground))]">
                    {companyName.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <h2 data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:149:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="149" data-component-end-line="157" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%7D"
              className={`mt-8 w-full max-w-[min(100%,22rem)] px-2 text-center text-xl font-semibold tracking-tight ${
                phase === 'primaryFill' || phase === 'fadeOut'
                  ? 'text-white drop-shadow-sm'
                  : 'text-[hsl(var(--foreground))]'
              }`}
            >
              {companyName}
            </h2>
            <p data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:158:12" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="158" data-component-end-line="166" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Welcome%20back%22%7D"
              className={`mt-2 text-sm ${
                phase === 'primaryFill' || phase === 'fadeOut'
                  ? 'text-white/85'
                  : 'text-[hsl(var(--muted-foreground))]'
              }`}
            >
              Welcome back
            </p>
          </div>
        </div>
      </div>

      <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:171:6" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="171" data-component-end-line="179" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`flex shrink-0 flex-col items-center pb-2 transition-opacity duration-500 ${
          phase === 'app' ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:176:8" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="176" data-component-end-line="178" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-1%20w-28%20overflow-hidden%20rounded-full%20bg-%5Bhsl(var(--muted))%5D%22%7D" className="h-1 w-28 overflow-hidden rounded-full bg-[hsl(var(--muted))]">
          <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:177:10" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="177" data-component-end-line="177" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20w-2%2F5%20rounded-full%20bg-%5Bhsl(var(--primary)%2F0.55)%5D%20animate-pulse%22%7D" className="h-full w-2/5 rounded-full bg-[hsl(var(--primary)/0.55)] animate-pulse" />
        </div>
      </div>
    </>
  );

  return (
    <>
      <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:185:6" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="185" data-component-end-line="196" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        role="status"
        aria-live="polite"
        aria-busy={phase !== 'fadeOut'}
        className={`lg:hidden fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-5 sm:px-10 transition-colors duration-[520ms] ease-out ${bgClass} ${overlayOpacity} transition-opacity duration-[560ms] ease-out`}
        style={{
          paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
        }}
      >
        {content}
      </div>

      <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:198:6" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="198" data-component-end-line="210" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`hidden lg:fixed lg:inset-0 lg:z-[100] lg:flex lg:flex-col lg:items-center lg:justify-center lg:overflow-hidden lg:px-12 transition-colors duration-[520ms] ease-out ${bgClass} ${overlayOpacity} transition-opacity duration-[560ms] ease-out`}
      >
        <div data-component-id="src/components/auth/AuthSessionLoadingSplash.tsx:201:8" data-component-path="src/components/auth/AuthSessionLoadingSplash.tsx" data-component-line="201" data-component-end-line="209" data-component-file="AuthSessionLoadingSplash.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20min-h-0%20w-full%20max-w-2xl%20flex-1%20flex-col%20items-center%20justify-center%22%7D"
          className="flex min-h-0 w-full max-w-2xl flex-1 flex-col items-center justify-center"
          style={{
            paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
          }}
        >
          {content}
        </div>
      </div>
    </>
  );
}
